/**
 * Vulnerability Findings Section - Collapsible section with all vulnerability cards
 */

import React, { useState, useRef, useEffect } from 'react';
import { VulnerabilityFinding } from '../../types';
import VulnerabilityCard from './VulnerabilityCard';
import { AlertIcon, ChevronDownIcon } from '../../../../components/Icons';
import './vulnerabilities.css';

interface VulnerabilityFindingsSectionProps {
  vulnerabilities: VulnerabilityFinding[];
  focusedVulnId?: string | null;
  onClearFocus?: () => void;
}

const VulnerabilityFindingsSection: React.FC<VulnerabilityFindingsSectionProps> = ({ vulnerabilities, focusedVulnId, onClearFocus }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const refs = useRef<{ [key: string]: HTMLDivElement | null}>({});

  useEffect(() => {
    if (focusedVulnId && refs.current[focusedVulnId]){
      setTimeout(() => {
        refs.current[focusedVulnId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200); // 100ms delay
    }
    if (focusedVulnId) setIsExpanded(true);
  }, [focusedVulnId]);

  // Handler to clear focus when a card is toggled manually
  const handleCardToggle = () => {
    if (onClearFocus) onClearFocus();
  };

  return (
    <div className="vuln-section">
      <div className="vuln-section__header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="vuln-section__title-row">
          <AlertIcon className="vuln-section__icon vuln-section__icon--error" />
          <h2 className="vuln-section__title">Detailed Vulnerability Findings</h2>
          <span className="vuln-section__badge">{vulnerabilities.length} Total</span>
        </div>
        <button className={`vuln-section__toggle ${isExpanded ? 'vuln-section__toggle--expanded' : ''}`}>
          <ChevronDownIcon className="vuln-section__toggle-icon" />
        </button>
      </div>

      {isExpanded && (
        <div className="vuln-section__content">
          {vulnerabilities.map((vulnerability) => (
            <div
              key={vulnerability.id}
              ref={el => refs.current[vulnerability.id] = el}
            >
              <VulnerabilityCard 
                vulnerability={vulnerability}
                isFocused={focusedVulnId === vulnerability.id}
                onToggle={handleCardToggle}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VulnerabilityFindingsSection;
